<script setup lang="ts">
  import type { SupportSession } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem
  } from '@gat-ship/vue-components'
  import { IconDotsHorizontal } from '@gat-ship/icons'
  import { useSupportModals } from '../providers/support-modals'
  import { useSupportSessionPermissions } from '../../composables/useSupportSessionPermissions'
  import { useSupportSessionAssignedContext } from '../../composables/useSupportSessionAssigned'

  defineProps<{
    session: SupportSession
  }>()

  const modals = useSupportModals()!
  const { canEndSupportSession, canStartSupportSession } =
    useSupportSessionPermissions()
  // Undefined on end-user surfaces (the my-sessions table) — the root
  // provider only provides the key for Gatship users, and only they see
  // the "Start Support Session" action that uses it.
  const supportSessionAssignedService = useSupportSessionAssignedContext()

  function endSupportSession(session: SupportSession) {
    modals.showEndSupportSession({ supportSession: session })
  }

  function startSupportSession(session: SupportSession) {
    supportSessionAssignedService?.commands.startSupportSession(
      session.sessionId
    )
  }

  function showSupportSessionDetails(session: SupportSession) {
    modals.showSupportSessionDetails({ supportSession: session })
  }
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button variant="ghost" size="icon" class="size-8">
        <IconDotsHorizontal class="size-4" />
        <span class="sr-only">Open session actions</span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem
        v-if="canEndSupportSession(session)"
        :data-testid="testIds.support.actions.endSupportSession"
        @click="endSupportSession(session)"
      >
        End Support Session
      </DropdownMenuItem>
      <DropdownMenuItem
        v-if="canStartSupportSession(session)"
        :data-testid="testIds.support.actions.startSupportSession"
        @click="startSupportSession(session)"
      >
        Start Support Session
      </DropdownMenuItem>
      <DropdownMenuItem
        :data-testid="testIds.support.actions.moreDetails"
        @click="showSupportSessionDetails(session)"
      >
        More Details
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
